import React, { useState, useEffect, useRef } from 'react';
import './EditableServiceCard.css';
import PricingCalendar from './PricingCalendar';
import PasoPlegable from './business/PasoPlegable';
import PieDePaso from './business/PieDePaso';
import { buscarNombreRepetido } from '../lib/servicios';

/**
 * Orden en el que se recorren los errores para llevar al usuario al primero que
 * falta. Es el mismo orden en el que los campos aparecen en pantalla.
 */
/** Si el sistema pide menos movimiento, el salto es instantáneo. */
const comportamientoDeScroll = () => (
  typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth'
);

const ORDEN_DE_ERRORES = [
  'name',
  'duration',
  'price',
  'maxAdvanceBookingDays',
  'paymentMethods',
  'assignedStaff'
];

/**
 * Los métodos que el negocio cobra a mano.
 *
 * Mercado Pago se confirma solo —el webhook avisa cuando el pago entró—, pero
 * con efectivo y transferencia el negocio tiene que decirle al cliente cómo
 * hacerle llegar la plata y el comprobante. Por eso el campo de instrucciones
 * sólo tiene sentido si eligió alguno de estos dos.
 *
 * Es la misma condición con la que DepositPaymentModal decide mostrárselas al
 * cliente (lee `depositConfig.confirmationInstructions` para 'transferencia' y
 * 'efectivo'): si acá se pidieran para otros casos, se estaría cargando un texto
 * que no se muestra en ningún lado.
 */
const METODOS_A_MANO = ['efectivo', 'transferencia'];

const NOMBRE_DEL_METODO = {
  efectivo: 'efectivo',
  transferencia: 'transferencia',
};

const EditableServiceCard = ({
  service, 
  onSave, 
  onCancel, 
  onDelete,
  businessHours, 
  availableStaff = [], 
  availableResources = [],
  availablePaymentMethods = [],
  // ¿El negocio conectó su cuenta de Mercado Pago? Sin eso no puede cobrar
  // señas por ahí, así que el método se muestra apagado y con un acceso para
  // conectarla en vez de dejar elegir algo que después falla al reservar.
  mercadoPagoConectado = false,
  onConectarMercadoPago,
  isEditing = true,
  showDeleteButton = false,
  userType = 'business',
  // Los servicios que el negocio ya tiene, para no dejar repetir un nombre. Se
  // pasa la lista entera y no los nombres sueltos porque el que se está
  // editando se reconoce por su id, y porque el aviso nombra al que choca.
  serviciosExistentes = []
}) => {
  const [editedService, setEditedService] = useState(service);
  const [errors, setErrors] = useState({});
  const [showDepositConfig, setShowDepositConfig] = useState(service.requiresDeposit || false);
  const [selectedCalendarSlot, setSelectedCalendarSlot] = useState(null);
  const [showDynamicPricing, setShowDynamicPricing] = useState(false); // Nuevo estado para colapsar/expandir
  const [saveError, setSaveError] = useState('');

  // Referencias a los campos obligatorios y a cada parte del formulario, para
  // poder scrollear hasta el primer error y para el índice de secciones.
  const camposRef = useRef({});
  /**
   * Qué parte está abierta. Una sola a la vez: al terminar una se cierra y se
   * abre la siguiente, así el formulario se recorre de a un tema por vez en vez
   * de aparecer entero de golpe.
   */
  const [parteAbierta, setParteAbierta] = useState('informacion');
  /**
   * Las partes por las que ya se pasó. Hace falta para las que no tienen campos
   * obligatorios —precios dinámicos, o la asignación automática de una cuenta
   * individual—: sin esto nunca se marcarían como hechas, porque no hay nada
   * que verificar.
   */
  const [parteVisitada, setParteVisitada] = useState([]);

  // Generar horas del día (de 00:00 a 23:00)
  const hours = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });

  // Días de la semana
  const daysOfWeek = [
    { id: 'lun', name: 'Lunes' },
    { id: 'mar', name: 'Martes' },
    { id: 'mie', name: 'Miércoles' },
    { id: 'jue', name: 'Jueves' },
    { id: 'vie', name: 'Viernes' },
    { id: 'sab', name: 'Sábado' },
    { id: 'dom', name: 'Domingo' }
  ];

  useEffect(() => {
    // Limpiar datos obsoletos antes de establecer el servicio
    const cleanedService = {
      ...service,
      assignedStaff: cleanObsoleteStaff(service.assignedStaff, availableStaff),
      assignedResources: cleanObsoleteResources(service.assignedResources, availableResources)
    };
    
    // 🧹 LIMPIAR variaciones duplicadas
    if (cleanedService.priceVariations && cleanedService.priceVariations.length > 0) {
      const originalCount = cleanedService.priceVariations.length;
      cleanedService.priceVariations = cleanDuplicateVariations(cleanedService.priceVariations);
      const cleanedCount = cleanedService.priceVariations.length;
      
      if (originalCount !== cleanedCount) {
        console.log('🧹 VARIACIONES DUPLICADAS LIMPIADAS:', {
          serviceName: service.name,
          originalCount,
          cleanedCount,
          removed: originalCount - cleanedCount
        });
      }
    }
    
    // 👤 AUTO-ASIGNAR PARA CUENTAS PERSONALES
    if (userType === 'individual') {
      // Auto-asignar el primer staff disponible (que debería ser el personal)
      if (availableStaff.length > 0 && (!cleanedService.assignedStaff || cleanedService.assignedStaff.length === 0)) {
        cleanedService.assignedStaff = [availableStaff[0].id];
        console.log('👤 AUTO-ASIGNANDO staff para cuenta personal:', availableStaff[0].name);
      }
      
      // Auto-asignar el primer recurso disponible si existe
      if (availableResources.length > 0 && (!cleanedService.assignedResources || cleanedService.assignedResources.length === 0)) {
        cleanedService.assignedResources = [availableResources[0].id];
        console.log('🏢 AUTO-ASIGNANDO recurso para cuenta personal:', availableResources[0].name);
      }
      
      // Para cuentas personales, siempre usar asignación automática
      cleanedService.staffSelectionMode = 'automatic';
      cleanedService.resourceSelectionMode = 'automatic';
    }
    
    // Ajustar modos de selección si es necesario para otros tipos de usuarios
    if (userType !== 'personal' && userType !== 'individual') {
      if (cleanedService.assignedStaff.length < 2) {
        cleanedService.staffSelectionMode = 'automatic';
      }
      
      if (cleanedService.assignedResources.length < 2) {
        cleanedService.resourceSelectionMode = 'automatic';
      }
    }
    
    setEditedService(cleanedService);
    setShowDepositConfig(service.requiresDeposit || false);
    
    // Inicializar estado de precios dinámicos basado en si ya tiene configuraciones
    const hasPriceVariations = cleanedService.priceVariations && cleanedService.priceVariations.length > 0;
    const isDynamicPricingEnabled = service.enableDynamicPricing || hasPriceVariations;
    setShowDynamicPricing(isDynamicPricingEnabled);
    
    console.log('🔄 Inicializando estado de precios dinámicos:', {
      serviceName: service.name,
      enableDynamicPricing: service.enableDynamicPricing,
      hasPriceVariations,
      priceVariationsCount: cleanedService.priceVariations?.length || 0,
      showDynamicPricing: isDynamicPricingEnabled
    });
    
    // Resetear errores cuando cambia el servicio
    setErrors({});
    setSaveError('');
  }, [service, availableStaff, availableResources]);

  const handleChange = (field, value) => {
    const updatedService = {
      ...editedService,
      [field]: value
    };
    
    setEditedService(updatedService);
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Clear save error when user makes changes
    if (saveError) {
      setSaveError('');
    }
    
    // NO auto-guardar - solo guardar cuando el usuario haga submit
  };

  const handleNestedChange = (parent, field, value) => {
    const updatedService = {
      ...editedService,
      [parent]: {
        ...editedService[parent],
        [field]: value
      }
    };
    
    setEditedService(updatedService);
    
    // NO auto-guardar - solo guardar cuando el usuario haga submit
  };

  /**
   * Devuelve el objeto de errores (vacío si está todo bien). No toca el estado:
   * de eso se encarga handleSave, que además necesita el objeto para saber a
   * qué campo llevar la pantalla.
   */
  const validateForm = () => {
    const newErrors = {};

    if (!editedService.name || editedService.name.trim() === '') {
      newErrors.name = 'El nombre del servicio es requerido';
    } else {
      // Dos servicios con el mismo nombre no se pueden distinguir al reservar, y
      // el backend los rechaza con un 409. Se avisa también acá para que el
      // error quede pegado al campo del nombre y aparezca antes de completar
      // todo el resto del formulario.
      const repetido = buscarNombreRepetido(serviciosExistentes, editedService.name, service.id);
      if (repetido) {
        newErrors.name = `Ya tenés un servicio que se llama "${repetido.name}". Elegí otro nombre.`;
      }
    }

    // Los mismos 5 a 480 que exige `validateServiceCreation` en el backend. Antes
    // acá sólo se pedía "mayor a 0", así que un servicio de 10 minutos pasaba
    // esta validación y moría en un 400 recién al guardar, después de completar
    // todo el resto. Si cambia el rango, cambia en los dos lados.
    const minutos = Number(editedService.duration);
    if (!Number.isFinite(minutos) || minutos < 5 || minutos > 480) {
      newErrors.duration = 'La duración debe ser entre 5 y 480 minutos';
    }

    // Con "a consultar" el precio deja de ser obligatorio: es el punto entero
    // del botón. Ver `alternarPrecioAConsultar`.
    if (!editedService.priceOnRequest && (!editedService.price || editedService.price <= 0)) {
      newErrors.price = 'El precio debe ser mayor a 0';
    }

    if (!editedService.maxAdvanceBookingDays || editedService.maxAdvanceBookingDays <= 0) {
      newErrors.maxAdvanceBookingDays = 'Los días de anticipación deben ser mayor a 0';
    }

    // VALIDACIÓN OBLIGATORIA: Debe tener al menos un personal asignado (excepto para usuarios individuales)
    if (userType !== 'individual' && (!editedService.assignedStaff || editedService.assignedStaff.length === 0)) {
      newErrors.assignedStaff = 'Debes asignar al menos un miembro del personal a este servicio';
    }

    // VALIDACIÓN OBLIGATORIA: Métodos de pago si requiere seña
    if (editedService.requiresDeposit) {
      const paymentMethods = editedService.depositConfig?.paymentMethods || [];
      if (paymentMethods.length === 0) {
        newErrors.paymentMethods = 'Debes seleccionar al menos un método de pago para las señas';
      }
    }

    return newErrors;
  };

  /**
   * Lleva la pantalla hasta el primer campo que falta completar y le da el foco.
   * Los bloques que no son un input (personal, métodos de pago) tienen
   * tabIndex={-1} justamente para poder recibir el foco acá.
   */
  const irAlPrimerError = (erroresActuales) => {
    const campo = ORDEN_DE_ERRORES.find((nombre) => erroresActuales[nombre]);
    if (!campo) return;

    // La parte que contiene el campo puede estar cerrada, y una parte cerrada
    // desmonta su contenido: el campo no está en el documento, así que primero
    // hay que abrirla. El setTimeout le da a React el turno para montarla; sin
    // él, camposRef todavía apunta a nada y no se iría a ningún lado.
    const parte = partes.find((p) => p.campos.includes(campo));
    if (parte) setParteAbierta(parte.id);

    setTimeout(() => {
      const elemento = camposRef.current[campo];
      if (!elemento) return;

      elemento.scrollIntoView({ behavior: comportamientoDeScroll(), block: 'center' });
      if (typeof elemento.focus === 'function') {
        // preventScroll: el salto lo hace scrollIntoView, no el foco
        elemento.focus({ preventScroll: true });
      }
    }, 0);
  };

  /**
   * Abre una parte y cierra la que estaba; volver a tocarla la cierra.
   *
   * No marca la parte como hecha: para eso está Continuar. Abrir algo para
   * mirarlo no es completarlo.
   */
  const alternarParte = (id) => {
    setParteAbierta((prev) => (prev === id ? null : id));
  };



  const handleCheckboxChange = (field) => {
    // Un servicio a consultar no puede pedir seña: el monto es un porcentaje de
    // un precio que no existe. La sección se ve, pero no responde.
    if (field === 'requiresDeposit' && editedService.priceOnRequest) return;

    const newValue = !editedService[field];

    if (field === 'requiresDeposit') {
      setShowDepositConfig(newValue);
      if (!newValue) {
        // Si se deshabilita el depósito, resetear la configuración
        const updatedService = {
          ...editedService,
          [field]: newValue,
          depositConfig: {
            type: 'percentage',
            amount: 50,
            // Ninguno activado de fábrica: el negocio elige cuáles acepta. Antes
            // venían los tres prendidos, así que un servicio nuevo ofrecía
            // Mercado Pago aunque el negocio nunca hubiera conectado su cuenta.
            paymentMethods: [],
            cancellationPolicy: 'Flexible - Devolución total hasta 24hs antes'
          }
        };
        setEditedService(updatedService);
        // NO auto-guardar
      } else {
        // Si se habilita el depósito y no existe configuración, inicializarla
        const updatedService = {
          ...editedService,
          [field]: newValue,
          depositConfig: editedService.depositConfig || {
            type: 'percentage',
            amount: 50,
            paymentMethods: [],
            cancellationPolicy: 'Flexible - Devolución total hasta 24hs antes'
          }
        };
        setEditedService(updatedService);
      }
    } else {
      handleChange(field, newValue);
    }
  };

  const handlePaymentMethodToggle = (method) => {
    const currentMethods = editedService.depositConfig?.paymentMethods || [];
    const newMethods = currentMethods.includes(method)
      ? currentMethods.filter(m => m !== method)
      : [...currentMethods, method];

    handleNestedChange('depositConfig', 'paymentMethods', newMethods);

    // Limpiar el error de métodos de pago en cuanto elige uno
    if (newMethods.length > 0 && errors.paymentMethods) {
      setErrors(prev => ({ ...prev, paymentMethods: undefined }));
      setSaveError('');
    }
  };

  // Función para limpiar variaciones duplicadas
  const cleanDuplicateVariations = (variations) => {
    if (!variations || !Array.isArray(variations)) return [];
    
    const uniqueVariations = [];
    const seenVariations = new Set();
    
    // Procesar en orden inverso para mantener la variación más reciente
    for (let i = variations.length - 1; i >= 0; i--) {
      const variation = variations[i];
      const days = variation.daysOfWeek || variation.days || [];
      const ranges = variation.timeRanges || [];
      
      // Crear una clave única basada en días y rangos de tiempo
      const key = `${days.sort().join(',')}_${ranges.map(r => `${r.start}-${r.end}`).join(',')}`;
      
      if (!seenVariations.has(key)) {
        seenVariations.add(key);
        uniqueVariations.unshift(variation); // Agregar al inicio para mantener el orden original
      } else {
        console.log('🧹 LIMPIANDO variación duplicada:', {
          removed: variation,
          key,
          reason: 'Mismos días y horarios que otra variación'
        });
      }
    }
    
    return uniqueVariations;
  };

  const handleAddPriceVariation = (newVariation) => {
    console.log('💰 EDITABLE SERVICE CARD: Recibiendo nueva variación:', {
      newVariation,
      hasDaysOfWeek: !!newVariation.daysOfWeek,
      hasDays: !!newVariation.days,
      daysOfWeek: newVariation.daysOfWeek,
      days: newVariation.days
    });
    
    const currentVariations = editedService.priceVariations || [];
    const updatedVariations = [...currentVariations, newVariation];
    
    // Activar automáticamente enableDynamicPricing cuando se agregan variaciones
    const updatedService = {
      ...editedService,
      priceVariations: updatedVariations,
      enableDynamicPricing: true
    };
    
    setEditedService(updatedService);
    
    console.log('💰 Agregando variación de precio:', {
      newVariation,
      totalVariations: updatedVariations.length,
      enableDynamicPricing: true
    });
  };

  const handleRemovePriceVariation = (id) => {
    const filtered = (editedService.priceVariations || []).filter(v => v.id !== id);
    
    // Si no quedan variaciones, mantener enableDynamicPricing pero permitir desactivar manualmente
    const updatedService = {
      ...editedService,
      priceVariations: filtered,
      // Solo desactivar si no quedan variaciones Y el usuario no lo activó manualmente
      enableDynamicPricing: filtered.length > 0 ? true : editedService.enableDynamicPricing
    };
    
    setEditedService(updatedService);
    
    console.log('🗑️ Eliminando variación de precio:', {
      removedId: id,
      remainingVariations: filtered.length,
      enableDynamicPricing: updatedService.enableDynamicPricing
    });
  };

  const handleVariationChange = (id, updatedVariation) => {
    const updated = (editedService.priceVariations || []).map(v => 
      v.id === id ? updatedVariation : v
    );
    handleChange('priceVariations', updated);
  };

  const handleAddTimeRange = (variationId) => {
    const updated = (editedService.priceVariations || []).map(v => {
      if (v.id === variationId) {
        return {
          ...v,
          timeRanges: [...(v.timeRanges || []), { start: '09:00', end: '10:00' }]
        };
      }
      return v;
    });
    handleChange('priceVariations', updated);
  };

  const handleRemoveTimeRange = (variationId, index) => {
    const updated = (editedService.priceVariations || []).map(v => {
      if (v.id === variationId) {
        return {
          ...v,
          timeRanges: v.timeRanges.filter((_, i) => i !== index)
        };
      }
      return v;
    });
    handleChange('priceVariations', updated);
  };

  const handleTimeRangeChange = (variationId, index, field, value) => {
    const updated = (editedService.priceVariations || []).map(v => {
      if (v.id === variationId) {
        const newTimeRanges = [...v.timeRanges];
        newTimeRanges[index] = { ...newTimeRanges[index], [field]: value };
        return { ...v, timeRanges: newTimeRanges };
      }
      return v;
    });
    handleChange('priceVariations', updated);
  };

  /**
   * "A consultar": el servicio se publica sin precio.
   *
   * Hay rubros donde el precio no se sabe hasta ver el trabajo —una reparación,
   * un tratamiento, un presupuesto—, y obligar a cargar un número lleva a
   * inventarlo o a poner 0, que en la página pública se lee como "gratis".
   *
   * Al activarlo se apagan la seña y los precios dinámicos, porque los dos se
   * calculan A PARTIR del precio: la seña es un porcentaje de él y una variación
   * lo multiplica. Sin precio las dos cuentas dan NaN. Se apagan de verdad, no
   * sólo se ocultan: si quedaran prendidos por debajo, el servicio se guardaría
   * pidiendo una seña de un monto imposible.
   *
   * El precio anterior no se borra del formulario, se deja de usar. Quien prueba
   * el botón y se arrepiente recupera lo que había escrito.
   */
  const alternarPrecioAConsultar = () => {
    const aConsultar = !editedService.priceOnRequest;

    setEditedService((previo) => ({
      ...previo,
      priceOnRequest: aConsultar,
      ...(aConsultar && {
        price: 0,
        requiresDeposit: false,
        enableDynamicPricing: false,
        depositConfig: null,
      }),
    }));

    if (aConsultar) {
      setShowDepositConfig(false);
      setShowDynamicPricing(false);
      // El error del precio deja de tener sentido apenas se aprieta el botón.
      setErrors((previos) => {
        const { price, paymentMethods, ...resto } = previos;
        return resto;
      });
    }
  };

  const handleDynamicPricingToggle = () => {
    // Sin precio no hay nada que variar. La sección se ve pero no responde.
    if (editedService.priceOnRequest) return;

    const newShowState = !showDynamicPricing;
    setShowDynamicPricing(newShowState);
    
    // Si se está desactivando y no hay variaciones, desactivar enableDynamicPricing
    if (!newShowState && (!editedService.priceVariations || editedService.priceVariations.length === 0)) {
      handleChange('enableDynamicPricing', false);
    }
    // Si se está activando, activar enableDynamicPricing
    else if (newShowState) {
      handleChange('enableDynamicPricing', true);
    }
    
    console.log('🎛️ Toggle precios dinámicos:', {
      showDynamicPricing: newShowState,
      enableDynamicPricing: newShowState || (editedService.priceVariations && editedService.priceVariations.length > 0),
      hasVariations: editedService.priceVariations?.length || 0,
      accion: newShowState ? 'ACTIVANDO' : 'DESACTIVANDO',
      nota: newShowState ? 'enableDynamicPricing = true' : 'enableDynamicPricing depende de si hay variaciones'
    });
  };

  const handleSave = async () => {
    const nuevosErrores = validateForm();
    setErrors(nuevosErrores);

    if (Object.keys(nuevosErrores).length > 0) {
      console.warn('⚠️ El servicio no se guarda:', Object.keys(nuevosErrores), { errores: nuevosErrores, userType });
      // El aviso de arriba tiene que describir el campo al que la pantalla
      // efectivamente salta, y `name` es el primero de ORDEN_DE_ERRORES: si el
      // nombre está repetido, ahí va a quedar parada. Decirle "faltan datos, te
      // llevamos al primer campo que hay que completar" mientras mira un campo
      // lleno la deja buscando qué le falta. Si además hay otros campos sin
      // completar, se dicen las dos cosas en vez de tapar una con la otra.
      const nombreRepetido = /se llama/.test(nuevosErrores.name || '');
      const hayOtros = Object.keys(nuevosErrores).length > 1;
      setSaveError(
        nombreRepetido
          ? `${nuevosErrores.name}${hayOtros ? ' Además hay otros datos obligatorios sin completar.' : ''}`
          : 'Faltan datos obligatorios. Te llevamos al primer campo que hay que completar.'
      );
      irAlPrimerError(nuevosErrores);
      return;
    }

    try {
      setSaveError('');
      await onSave(editedService);
      // Si llegamos aquí, el guardado fue exitoso
      // El componente padre cerrará el modal y reseteará el estado
    } catch (error) {
      console.error('Error al guardar:', error);
      // Se muestra el mensaje que vino del backend, no uno genérico: "Error al
      // guardar, intentá de nuevo" no dice qué corregir, y si el motivo es un
      // límite del plan o un nombre repetido, reintentar no lo arregla nunca.
      setSaveError(error?.message || 'Error al guardar el servicio. Por favor, intenta de nuevo.');
    }
  };



  const handleStaffToggle = (staffId) => {
    const currentStaff = editedService.assignedStaff || [];
    const newStaff = currentStaff.includes(staffId)
      ? currentStaff.filter(id => id !== staffId)
      : [...currentStaff, staffId];
    
    // Actualizar el servicio con el nuevo staff
    const updatedService = {
      ...editedService,
      assignedStaff: newStaff
    };

    // Si solo queda menos de 2 staff, cambiar a asignación automática
    if (newStaff.length < 2) {
      updatedService.staffSelectionMode = 'automatic';
    }

    setEditedService(updatedService);
    
    // Limpiar error de personal cuando el usuario selecciona personal
    if (newStaff.length > 0 && errors.assignedStaff) {
      setErrors(prev => ({
        ...prev,
        assignedStaff: undefined
      }));
      setSaveError('');
    }
    
    // NO auto-guardar - solo guardar cuando el usuario haga submit
  };

  const handleResourceToggle = (resourceId) => {
    const currentResources = editedService.assignedResources || [];
    const newResources = currentResources.includes(resourceId)
      ? currentResources.filter(id => id !== resourceId)
      : [...currentResources, resourceId];
    
    // Actualizar el servicio con los nuevos recursos
    const updatedService = {
      ...editedService,
      assignedResources: newResources
  };

    // Si solo queda menos de 2 recursos, cambiar a asignación automática
    if (newResources.length < 2) {
      updatedService.resourceSelectionMode = 'automatic';
    }
    
    setEditedService(updatedService);
    
    // NO auto-guardar - solo guardar cuando el usuario haga submit
  };

  // Función para limpiar staff obsoletos
  const cleanObsoleteStaff = (assignedStaff, availableStaff) => {
    if (!assignedStaff || !Array.isArray(assignedStaff)) return [];
    
    const availableStaffIds = availableStaff.map(staff => staff.id);
    const cleanedStaff = assignedStaff.filter(staffId => availableStaffIds.includes(staffId));
    
    const removedStaff = assignedStaff.filter(id => !availableStaffIds.includes(id));
    if (removedStaff.length > 0) {
      console.log('🧹 STAFF OBSOLETOS ELIMINADOS:', {
        original: assignedStaff,
        cleaned: cleanedStaff,
        removed: removedStaff
      });
    }
    
    return cleanedStaff;
  };

  // Función para limpiar recursos obsoletos
  const cleanObsoleteResources = (assignedResources, availableResources) => {
    if (!assignedResources || !Array.isArray(assignedResources)) return [];
    
    const availableResourceIds = availableResources.map(resource => resource.id);
    const cleanedResources = assignedResources.filter(resourceId => availableResourceIds.includes(resourceId));
    
    const removedResources = assignedResources.filter(id => !availableResourceIds.includes(id));
    if (removedResources.length > 0) {
      console.log('🧹 RECURSOS OBSOLETOS ELIMINADOS:', {
        original: assignedResources,
        cleaned: cleanedResources,
        removed: removedResources
      });
    }
    
    return cleanedResources;
  };



  // Función para verificar si un slot tiene precio dinámico
  const getSlotPriceVariation = (dayId, hour) => {
    if (!editedService.priceVariations) return null;
    
    const hourNum = parseInt(hour.split(':')[0]);
    
    for (const variation of editedService.priceVariations) {
      if (!variation.days.includes(dayId)) continue;
      
      for (const range of variation.timeRanges || []) {
        const startHour = parseInt(range.start.split(':')[0]);
        const endHour = parseInt(range.end.split(':')[0]);
        
        if (hourNum >= startHour && hourNum < endHour) {
        return variation;
        }
      }
    }
    return null;
  };

  // Función para manejar click en un slot del calendario
  const handleCalendarSlotClick = (dayId, hour) => {
    const existingVariation = getSlotPriceVariation(dayId, hour);
    
    if (existingVariation) {
      // Si ya existe, mostrar opciones de editar/eliminar
      setSelectedCalendarSlot({
        dayId,
        hour,
        variation: existingVariation
      });
    } else {
      // Si no existe, crear nueva variación para este slot
      const hourNum = parseInt(hour.split(':')[0]);
      const newVariation = {
        id: Date.now(),
        name: `${daysOfWeek.find(d => d.id === dayId).name} ${hour}`,
        price: editedService.price || 0,
        days: [dayId],
        timeRanges: [{
          start: hour,
          end: `${(hourNum + 1).toString().padStart(2, '0')}:00`
        }]
      };
      
      const currentVariations = editedService.priceVariations || [];
      handleChange('priceVariations', [...currentVariations, newVariation]);
    }
  };

  // Usar staff y resources de las props
  const staffList = availableStaff || [];

  const resourcesList = availableResources || [];

  // Las partes del formulario, en orden. Cada una declara qué campos
  // obligatorios contiene, así el índice de arriba puede marcar en rojo la
  // parte donde falta algo.
  const partes = [
    {
      id: 'informacion',
      numero: 1,
      titulo: 'Información del servicio',
      campos: ['name', 'duration', 'price'],
      /* `price` se queda en `campos` porque es la clave con la que validateForm
         reporta el error y con la que irAlPrimerError encuentra a dónde llevar
         la pantalla. Pero el tilde de "parte completa" necesita una condición
         que la lista no puede expresar: con "a consultar" no hay precio y la
         parte está completa igual. Sin esto, un servicio sin precio quedaba
         marcado como incompleto para siempre. */
      completa: (s) =>
        Boolean(s.name) && Boolean(s.duration) && (s.priceOnRequest || Boolean(s.price)),
    },
    { id: 'reserva', numero: 2, titulo: 'Opciones de reserva', campos: ['maxAdvanceBookingDays'] },
    {
      id: 'sena',
      numero: 3,
      titulo: 'Seña y pagos',
      campos: ['paymentMethods'],
      /* Esta parte no se puede resolver con la lista de campos, y por eso nunca
         se marcaba como completa: `campos` se lee contra `editedService[campo]`,
         pero los métodos de pago NO viven ahí —viven en
         `editedService.depositConfig.paymentMethods`, que es donde los escribe
         handlePaymentMethodToggle—, así que `editedService.paymentMethods` era
         siempre undefined y el tilde no aparecía nunca.

         'paymentMethods' se queda en `campos` igual porque es la clave con la
         que validateForm reporta el error y con la que irAlPrimerError encuentra
         a qué parte llevar la pantalla.

         Y hay una segunda condición que una lista de campos tampoco expresa: si
         el servicio no pide seña no hay ningún método que elegir, así que la
         parte está completa apenas se la visita. Esto replica exactamente lo que
         valida validateForm. */
      completa: (s) =>
        !s.requiresDeposit || (s.depositConfig?.paymentMethods || []).length > 0,
    },
    { id: 'precios', numero: 4, titulo: 'Precios dinámicos', campos: [] },
    {
      id: 'personal',
      numero: 5,
      titulo: userType === 'individual' ? 'Asignación automática' : 'Personal y recursos',
      campos: userType === 'individual' ? [] : ['assignedStaff']
    }
  ];

  /* parteConError se eliminó junto con el índice de partes: era lo que pintaba
     en rojo el botón del índice. Ahora el error se resuelve abriendo la parte
     que lo contiene, que es más directo que marcarla y esperar que la abran. */

  /**
   * Una parte está hecha cuando se pasó por ella Y sus campos obligatorios
   * tienen algo.
   *
   * Las dos condiciones, no una. Mirando solo los campos, "Opciones de reserva"
   * aparecía con el tilde puesto apenas se abría un servicio nuevo, porque la
   * anticipación viene con 30 días por defecto: el tilde decía "listo" sin que
   * nadie hubiera hecho nada, y entonces no distingue lo terminado de lo que
   * todavía no se miró. Se marca al apretar Continuar, que es cuando uno da por
   * cerrada la parte.
   */
  const parteCompleta = (id) => {
    const parte = partes.find((p) => p.id === id);
    if (!parte) return false;
    if (!parteVisitada.includes(id)) return false;
    // Las partes cuya condición no entra en una lista de campos traen la suya.
    if (parte.completa) return parte.completa(editedService);
    return parte.campos.every((campo) => {
      const valor = editedService[campo];
      if (Array.isArray(valor)) return valor.length > 0;
      return valor !== undefined && valor !== null && valor !== '';
    });
  };

  /** Lo cargado en esa parte, para verlo sin tener que abrirla. */
  const resumenDeParte = (id) => {
    const s = editedService;
    if (id === 'informacion') {
      const precio = s.priceOnRequest ? 'A consultar' : (s.price && `$${s.price}`);
      return [s.name, s.duration && `${s.duration} min`, precio]
        .filter(Boolean).join(' · ');
    }
    if (id === 'reserva') {
      return s.maxAdvanceBookingDays ? `Hasta ${s.maxAdvanceBookingDays} días antes` : '';
    }
    if (id === 'sena') {
      // Misma corrección que en `partes`: los métodos están en depositConfig,
      // no sueltos en el servicio. Acá el síntoma era que el resumen de la
      // parte cerrada quedaba siempre vacío.
      if (!s.requiresDeposit) return 'Sin seña';
      const metodos = (s.depositConfig?.paymentMethods || []).length;
      return metodos ? `${metodos} ${metodos === 1 ? 'medio de pago' : 'medios de pago'}` : '';
    }
    if (id === 'precios') {
      const variantes = (s.priceVariations || []).length;
      return variantes ? `${variantes} ${variantes === 1 ? 'variante' : 'variantes'}` : 'Sin variantes';
    }
    if (id === 'personal') {
      if (userType === 'individual') return 'Te asignás vos';
      const gente = (s.assignedStaff || []).length;
      return gente ? `${gente} ${gente === 1 ? 'persona' : 'personas'}` : '';
    }
    return '';
  };

  /**
   * Pie de cada parte: el botón que la cierra y abre la siguiente.
   *
   * El botón en sí vive en business/PieDePaso.jsx, porque los datos básicos del
   * negocio son el mismo acordeón y usan el mismo pie. Lo que decide acá es qué
   * hace: cerrar y abrir la que sigue, o guardar si es la última.
   */
  const pieDeParte = (id) => {
    const indice = partes.findIndex((p) => p.id === id);
    const siguiente = partes[indice + 1];

    if (!siguiente) {
      return <PieDePaso texto="Guardar servicio" onClick={handleSave} />;
    }

    return (
      <PieDePaso
        onClick={() => {
          setParteVisitada((prev) => (prev.includes(id) ? prev : [...prev, id]));
          setParteAbierta(siguiente.id);
        }}
      />
    );
  };

  /** Cuáles de los métodos que el negocio cobra a mano están elegidos. Decide
      si se pide el texto de instrucciones y cómo se lo nombra. Sale de
      METODOS_A_MANO y no de los elegidos, para que el orden sea siempre el
      mismo ("efectivo y transferencia") y no dependa de en qué orden los tocó. */
  const metodosAMano = METODOS_A_MANO.filter((metodo) =>
    (editedService.depositConfig?.paymentMethods || []).includes(metodo)
  );

  return (
    <div className="editable-service-card">
      <div className="editable-card-header">
        <h3>Configurar Servicio</h3>
        <div className="editable-card-actions">
          {showDeleteButton && onDelete && (
            <button
              type="button"
              className="btn-delete-editable"
              onClick={onDelete}
              title="Eliminar servicio"
            >
              🗑️ Eliminar
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
          >
            Salir
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
          >
            Guardar
          </button>
        </div>
      </div>

      {saveError && (
        <div className="save-error-message" role="alert">
          ❌ {saveError}
        </div>
      )}

      <div className="editable-card-body">
        {/* Acá había un índice de partes con un botón por sección, que llevaba
            scrolleando a cada una. Sobra: ahora las partes son plegables y sus
            propios títulos son el índice, con la ventaja de que se ve cuál está
            hecha y cuál falta sin abrirla. */}

        {/* Parte 1: información básica del servicio */}
        <PasoPlegable
          numero={1}
          titulo="Información del servicio"
          descripcion="Nombre, duración y precio."
          resumen={resumenDeParte("informacion")}
          completo={parteCompleta("informacion")}
          abierto={parteAbierta === "informacion"}
          onToggle={() => alternarParte("informacion")}
        >

          <div className="section-body">
            <div className="basic-info-grid">
              <div className="editable-field">
                <label htmlFor={`serviceName-${service.id}`}>Nombre del servicio *</label>
                <input
                  id={`serviceName-${service.id}`}
                  ref={(el) => { camposRef.current.name = el; }}
                  type="text"
                  value={editedService.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Ej: Corte de pelo"
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              <div className="editable-field">
                <label htmlFor={`serviceDuration-${service.id}`}>Duración (minutos) *</label>
                <input
                  id={`serviceDuration-${service.id}`}
                  ref={(el) => { camposRef.current.duration = el; }}
                  type="number"
                  value={editedService.duration || ''}
                  onChange={(e) => handleChange('duration', parseInt(e.target.value) || 0)}
                  placeholder="30"
                  min="1"
                  className={errors.duration ? 'error' : ''}
                />
                {errors.duration && <span className="field-error">{errors.duration}</span>}
              </div>

              <div className="editable-field">
                <label htmlFor={`servicePrice-${service.id}`}>
                  {editedService.priceOnRequest ? 'Precio base' : 'Precio base *'}
                </label>
                <div className="campo-precio">
                  <input
                    id={`servicePrice-${service.id}`}
                    ref={(el) => { camposRef.current.price = el; }}
                    type="number"
                    value={editedService.priceOnRequest ? '' : (editedService.price || '')}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Solo actualizar si el valor no está vacío o es un número válido
                      if (value === '' || !isNaN(parseFloat(value))) {
                        handleChange('price', value === '' ? 0 : parseFloat(value));
                      }
                    }}
                    placeholder={editedService.priceOnRequest ? 'A consultar' : '1000'}
                    min="0"
                    step="0.01"
                    disabled={editedService.priceOnRequest}
                    className={errors.price ? 'error' : ''}
                  />
                  {/* El botón va PEGADO al precio y no en otra sección: es la
                      respuesta a la pregunta que se está haciendo justo ahí
                      ("¿qué pongo si no sé cuánto sale?"), y en otro lado no se
                      encuentra en el momento en que hace falta. */}
                  <button
                    type="button"
                    className={`boton-sin-precio ${editedService.priceOnRequest ? 'activo' : ''}`}
                    onClick={alternarPrecioAConsultar}
                    aria-pressed={editedService.priceOnRequest || false}
                  >
                    {editedService.priceOnRequest ? '✓ A consultar' : 'No sé el precio'}
                  </button>
                </div>
                {editedService.priceOnRequest ? (
                  <span className="field-nota">
                    El servicio se publica sin precio: tus clientes no van a ver
                    ningún importe en la tarjeta.
                  </span>
                ) : (
                  errors.price && <span className="field-error">{errors.price}</span>
                )}
              </div>

              <div className="editable-field full-width">
                <label htmlFor={`serviceDescription-${service.id}`}>Descripción</label>
                <textarea
                  id={`serviceDescription-${service.id}`}
                  value={editedService.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Contá qué incluye el servicio..."
                  rows="3"
                />
              </div>
            </div>
          </div>
          {pieDeParte("informacion")}
        </PasoPlegable>

        {/* Parte 2: opciones de reserva */}
        <PasoPlegable
          numero={2}
          titulo="Opciones de reserva"
          descripcion="Anticipación y cancelación."
          resumen={resumenDeParte("reserva")}
          completo={parteCompleta("reserva")}
          abierto={parteAbierta === "reserva"}
          onToggle={() => alternarParte("reserva")}
        >

          <div className="section-body">
            <div className="reservation-options-row">
              <div className="form-group">
                <label htmlFor={`maxAdvanceDays-${service.id}`}>
                  Días de anticipación máxima *
                </label>
                <input
                  id={`maxAdvanceDays-${service.id}`}
                  ref={(el) => { camposRef.current.maxAdvanceBookingDays = el; }}
                  type="number"
                  min="1"
                  max="365"
                  value={editedService.maxAdvanceBookingDays || ''}
                  onChange={(e) => handleChange('maxAdvanceBookingDays', parseInt(e.target.value) || 0)}
                  placeholder="30"
                  className={errors.maxAdvanceBookingDays ? 'error' : ''}
                />
                <small className="field-help">
                  ¿Con cuántos días de anticipación se puede reservar?
                </small>
                {errors.maxAdvanceBookingDays && <span className="field-error">{errors.maxAdvanceBookingDays}</span>}
              </div>

              {/* Con la misma forma que el campo de al lado —rótulo arriba,
                  control abajo, ayuda debajo— para que las dos columnas
                  compartan las mismas líneas. Antes era una caja sola que
                  arrancaba 37px más arriba que el input y medía 66px contra 48:
                  se veía descolgada. */}
              <div className="form-group">
                {/* <label> y no <span>: los rótulos de los otros campos son <label> y
                    reciben reglas globales que un span no —margen inferior y otra
                    altura de línea—, así que con un span la columna derecha
                    arrancaba 15px más arriba que la izquierda. */}
                <label className="form-group-titulo">Cancelaciones</label>
                <label
                  className="checkbox-item"
                  htmlFor={`allowCancellations-${service.id}`}
                >
                  <input
                    type="checkbox"
                    id={`allowCancellations-${service.id}`}
                    checked={editedService.allowCancellation || false}
                    onChange={(e) => handleChange('allowCancellation', e.target.checked)}
                  />
                  Permitir cancelaciones
                </label>
                <small className="field-help">
                  Los clientes pueden cancelar sus turnos reservados
                </small>
              </div>
            </div>
          </div>
          {pieDeParte("reserva")}
        </PasoPlegable>

        {/* Parte 3: seña y pagos */}
        <PasoPlegable
          numero={3}
          titulo="Seña y pagos"
          descripcion="Si pedís una seña para confirmar el turno."
          resumen={resumenDeParte("sena")}
          completo={parteCompleta("sena")}
          abierto={parteAbierta === "sena"}
          onToggle={() => alternarParte("sena")}
        >

          {/* Bloqueada, no escondida.
              Esconderla dejaría a alguien buscando una función que la landing
              anuncia y creyendo que no existe. Se ve, con el motivo escrito, y
              se destraba sacando "A consultar" — que es la acción que lo
              resuelve, y por eso está acá adentro y no hay que ir a buscarla. */}
          {editedService.priceOnRequest && (
            <div className="bloqueado-sin-precio">
              <p>
                <strong>La seña necesita un precio.</strong> El monto se calcula
                como un porcentaje del precio del servicio, y este servicio es a
                consultar.
              </p>
              <button type="button" className="boton-destrabar" onClick={alternarPrecioAConsultar}>
                Poner un precio para habilitarla
              </button>
            </div>
          )}

          <div className={`deposit-options ${editedService.priceOnRequest ? 'inhabilitado' : ''}`}>
            <div
              className={`checkbox-option ${editedService.requiresDeposit ? 'primary' : ''}`}
              onClick={() => handleCheckboxChange('requiresDeposit')}
            >
              <input
                type="checkbox"
                checked={editedService.requiresDeposit || false}
                onChange={() => {}}
              />
              <span className="checkbox-label">Requiere seña para reservar</span>
            </div>

            {showDepositConfig && (
              <div className="deposit-config">
                <div className="deposit-type">
                  <span className="config-label">Tipo de seña:</span>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name={`depositType-${service.id}`}
                        value="percentage"
                        checked={editedService.depositConfig?.type === 'percentage'}
                        onChange={(e) => handleNestedChange('depositConfig', 'type', e.target.value)}
                      />
                      Porcentaje
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name={`depositType-${service.id}`}
                        value="fixed"
                        checked={editedService.depositConfig?.type === 'fixed'}
                        onChange={(e) => handleNestedChange('depositConfig', 'type', e.target.value)}
                      />
                      Monto fijo
                    </label>
                  </div>
                </div>

                <div className="deposit-amount">
                  <span className="config-label">Monto:</span>
                  <div className="amount-input">
                    <input
                      type="number"
                      value={editedService.depositConfig?.amount || 50}
                      onChange={(e) => handleNestedChange('depositConfig', 'amount', parseFloat(e.target.value) || 0)}
                      min="0"
                      max={editedService.depositConfig?.type === 'percentage' ? 100 : undefined}
                    />
                    <span className="amount-suffix">
                      {editedService.depositConfig?.type === 'percentage' ? '%' : '$'}
                    </span>
                  </div>
                  {editedService.depositConfig?.type === 'percentage' && (
                    <span className="amount-preview">
                      (${((editedService.price || 0) * (editedService.depositConfig?.amount || 50) / 100).toFixed(2)})
                    </span>
                  )}
                </div>

                <div
                  className={`payment-methods ${errors.paymentMethods ? 'block-error' : ''}`}
                  ref={(el) => { camposRef.current.paymentMethods = el; }}
                  tabIndex={-1}
                >
                  <span className="config-label">
                    Métodos de pago aceptados: <span className="required-mark">*</span>
                  </span>
                  <div className="payment-buttons">
                    {availablePaymentMethods.length > 0 ? (
                      availablePaymentMethods.map(method => {
                        const elegido = (editedService.depositConfig?.paymentMethods || []).includes(method);
                        // Mercado Pago sin cuenta conectada no se puede elegir:
                        // el cliente llegaría a pagar y el backend cortaría con
                        // "MercadoPago no configurado".
                        const bloqueado = method === 'mercadopago' && !mercadoPagoConectado;

                        return (
                          <button
                            key={method}
                            type="button"
                            className={`payment-btn ${elegido ? 'selected' : ''} ${bloqueado ? 'payment-btn-bloqueado' : ''}`}
                            onClick={() => (bloqueado ? onConectarMercadoPago?.() : handlePaymentMethodToggle(method))}
                            title={bloqueado ? 'Conectá tu cuenta de Mercado Pago para cobrar señas por ahí' : undefined}
                          >
                            {method.charAt(0).toUpperCase() + method.slice(1)}
                            {bloqueado && <span className="payment-btn-aviso">Conectar cuenta</span>}
                          </button>
                        );
                      })
                    ) : (
                      <div className="payment-empty">
                        No hay métodos de pago configurados. Configuralos en el paso anterior.
                      </div>
                    )}
                  </div>
                  {errors.paymentMethods && (
                    <span className="field-error">{errors.paymentMethods}</span>
                  )}
                </div>

                <div className="cancellation-policy">
                  <span className="config-label">Política de cancelación:</span>
                  <select
                    className="cancellation-select"
                    value={editedService.depositConfig?.cancellationPolicy || 'flexible'}
                    onChange={(e) => handleNestedChange('depositConfig', 'cancellationPolicy', e.target.value)}
                  >
                    <option value="flexible">Flexible - Devolución total hasta 24hs antes</option>
                    <option value="moderate">Moderada - Devolución 50% hasta 48hs antes</option>
                    <option value="strict">Estricta - Sin devolución</option>
                  </select>
                </div>

                {/* Sólo con efectivo o transferencia elegidos. Antes aparecía
                    siempre que el servicio pidiera seña, así que a un negocio
                    que cobra únicamente por Mercado Pago se le pedía escribir
                    cómo recibe una transferencia que no acepta —y ese texto no
                    se le muestra a nadie, porque el modal del cliente sólo lo
                    saca para esos dos métodos. */}
                {metodosAMano.length > 0 && (
                  <div className="payment-confirmation">
                    <span className="config-label">
                      ¿Cómo confirmás los pagos por{' '}
                      {metodosAMano.map((m) => NOMBRE_DEL_METODO[m]).join(' y ')}?
                    </span>
                    <textarea
                      className="confirmation-instructions"
                      placeholder="Ej: Mandame el comprobante por WhatsApp al +54 9 11 1234-5678, o traelo al local antes del turno"
                      value={editedService.depositConfig?.confirmationInstructions || ''}
                      onChange={(e) => handleNestedChange('depositConfig', 'confirmationInstructions', e.target.value)}
                      rows="3"
                    />
                    <small className="field-help">
                      Se le muestra al cliente cuando elige{' '}
                      {metodosAMano.map((m) => NOMBRE_DEL_METODO[m]).join(' o ')}.
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>
          {pieDeParte("sena")}
        </PasoPlegable>

        {/* Parte 4: precios dinámicos con calendario */}
        <PasoPlegable
          numero={4}
          titulo="Precios dinámicos"
          descripcion="Opcional: cobrá distinto según el día y la hora."
          resumen={resumenDeParte("precios")}
          completo={parteCompleta("precios")}
          abierto={parteAbierta === "precios"}
          onToggle={() => alternarParte("precios")}
        >

          {/* Igual que la seña: se ve bloqueada y con el motivo, no escondida. */}
          {editedService.priceOnRequest && (
            <div className="bloqueado-sin-precio">
              <p>
                <strong>Los precios dinámicos necesitan un precio.</strong> Lo que
                se configura es cuánto sube o baja el precio base según el día y
                la hora, y este servicio es a consultar.
              </p>
              <button type="button" className="boton-destrabar" onClick={alternarPrecioAConsultar}>
                Poner un precio para habilitarlos
              </button>
            </div>
          )}

          <div className={`dynamic-pricing-options ${editedService.priceOnRequest ? 'inhabilitado' : ''}`}>
            <div
              className={`checkbox-option ${showDynamicPricing ? 'primary' : ''}`}
              onClick={() => handleDynamicPricingToggle()}
            >
              <input
                type="checkbox"
                checked={showDynamicPricing}
                onChange={() => {}}
              />
              <span className="checkbox-label">Configurar precios dinámicos</span>
            </div>
          </div>

          <div className={`dynamic-pricing-wrapper ${showDynamicPricing ? 'expanded' : 'collapsed'}`}>
            <div className="dynamic-pricing-content">
              <PricingCalendar
                basePrice={editedService.price || 0}
                businessHours={businessHours}
                priceVariations={editedService.priceVariations || []}
                onAddVariation={handleAddPriceVariation}
                onEditVariation={handleVariationChange}
                onRemoveVariation={handleRemovePriceVariation}
              />
            </div>
          </div>
          {pieDeParte("precios")}
        </PasoPlegable>

        {/* Parte 5 (cuentas individuales): asignación automática */}
        {userType === 'individual' && (
        <PasoPlegable
          numero={5}
          titulo="Asignación automática"
          descripcion="No hace falta configurar nada más."
          resumen={resumenDeParte("personal")}
          completo={parteCompleta("personal")}
          abierto={parteAbierta === "personal"}
          onToggle={() => alternarParte("personal")}
        >

          <div className="personal-account-info">
            <div className="info-card">
              <div className="info-icon">✅</div>
              <div className="info-content">
                <h5>Personal y recursos auto-asignados</h5>
                <p>Como tenés una cuenta individual, el sistema te asigna a vos como responsable del servicio y usa tu lugar de atención configurado.</p>
                {availableStaff.length > 0 && (
                  <div className="assigned-info">
                    <strong>Personal asignado:</strong> {availableStaff[0]?.name}
                  </div>
                )}
                {availableResources.length > 0 && (
                  <div className="assigned-info">
                    <strong>Lugar de atención:</strong> {availableResources[0]?.name}
                  </div>
                )}
              </div>
            </div>
          </div>
          {pieDeParte("personal")}
        </PasoPlegable>
        )}

        {/* Parte 5: personal y recursos (cuentas de negocio) */}
        {userType !== 'individual' && (
        <PasoPlegable
          numero={5}
          titulo="Personal y recursos"
          descripcion="Quién lo atiende y con qué recursos."
          resumen={resumenDeParte("personal")}
          completo={parteCompleta("personal")}
          abierto={parteAbierta === "personal"}
          onToggle={() => alternarParte("personal")}
        >

          <div
            className={`assignment-group ${errors.assignedStaff ? 'block-error' : ''}`}
            ref={(el) => { camposRef.current.assignedStaff = el; }}
            tabIndex={-1}
          >
            <span className="config-label">Personal asignado: <span className="required-mark">*</span></span>
            <div className="assignment-subtitle">
              <span>⚠️ <strong>Es obligatorio asignar al menos un miembro del personal</strong> para poder guardar el servicio.</span>
            </div>
            {staffList.length > 0 ? (
              <div className="selection-buttons">
                {staffList.map(staff => (
                  <button
                    key={staff.id}
                    type="button"
                    className={`selection-btn ${(editedService.assignedStaff || []).includes(staff.id) ? 'selected' : ''}`}
                    onClick={() => handleStaffToggle(staff.id)}
                  >
                    {staff.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No hay personal configurado</p>
                <small>Agregá personal desde el paso anterior del onboarding para poder asignarlo a tus servicios</small>
              </div>
            )}
            {errors.assignedStaff && (
              <span className="field-error">❌ {errors.assignedStaff}</span>
            )}
            {(editedService.assignedStaff || []).length >= 2 && (
              <div className="client-choice-option">
                <div className="choice-section-title">¿Cómo elige el cliente el personal?</div>
                <div className="radio-options">
                  <label className="radio-choice">
                    <input
                      type="radio"
                      name={`staffChoice-${service.id}`}
                      value="required"
                      checked={editedService.staffSelectionMode === 'required'}
                      onChange={() => handleChange('staffSelectionMode', 'required')}
                    />
                    <div>
                      <span className="radio-label">Obligatorio elegir</span>
                      <span className="radio-help">El cliente debe elegir un profesional específico</span>
                    </div>
                  </label>
                  
                  <label className="radio-choice">
                    <input
                      type="radio"
                      name={`staffChoice-${service.id}`}
                      value="optional"
                      checked={editedService.staffSelectionMode === 'optional'}
                      onChange={() => handleChange('staffSelectionMode', 'optional')}
                    />
                    <div>
                      <span className="radio-label">Opcional elegir</span>
                      <span className="radio-help">El cliente puede elegir o dejar que el sistema asigne</span>
                    </div>
                  </label>
                  
                  <label className="radio-choice">
                    <input
                      type="radio"
                      name={`staffChoice-${service.id}`}
                      value="automatic"
                      checked={editedService.staffSelectionMode === 'automatic' || !editedService.staffSelectionMode}
                      onChange={() => handleChange('staffSelectionMode', 'automatic')}
                    />
                    <div>
                      <span className="radio-label">Asignación automática</span>
                      <span className="radio-help">El sistema asigna automáticamente el personal disponible</span>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Con UNA sola persona asignada no hay modo que elegir —el selector
                de arriba aparece recién con dos—, así que este servicio nunca
                mostraba el paso del profesional y el cliente no llegaba a saber
                con quién se iba a atender.

                En un consultorio eso no es un detalle: quien pide turno con la
                nutricionista quiere ver que el turno quedó con ella. Con esto
                prendido, el paso se muestra igual, con esa persona ya elegida —no
                hay nada que decidir— y el dato viaja al resumen, a la
                confirmación y al turno que ve el negocio en su panel. */}
            {(editedService.assignedStaff || []).length === 1 && (
              <div className="client-choice-option">
                <label className="checkbox-item" htmlFor={`mostrarProfesional-${service.id}`}>
                  <input
                    type="checkbox"
                    id={`mostrarProfesional-${service.id}`}
                    checked={editedService.alwaysShowStaff || false}
                    onChange={(e) => handleChange('alwaysShowStaff', e.target.checked)}
                  />
                  Mostrar quién atiende, aunque sea una sola persona
                </label>
                <small className="field-help">
                  El cliente ve el nombre del profesional antes de confirmar. Útil
                  en consultorios, donde saber con quién es parte del turno.
                </small>
              </div>
            )}
          </div>

          <div className="assignment-group">
            <span className="config-label">Recursos necesarios:</span>
            <div className="assignment-subtitle">
              <span>No es obligatorio asignar recursos al servicio. Si no asignás ninguno, el servicio se considera disponible sin requerir recursos específicos.</span>
            </div>
            {resourcesList.length > 0 ? (
              <div className="selection-buttons">
                {resourcesList.map(resource => (
                  <button
                    key={resource.id}
                    type="button"
                    className={`selection-btn ${(editedService.assignedResources || []).includes(resource.id) ? 'selected' : ''}`}
                    onClick={() => handleResourceToggle(resource.id)}
                  >
                    {resource.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No hay recursos configurados</p>
                <small>Agregá recursos desde la sección "Recursos" del Dashboard para poder asignarlos a tus servicios</small>
              </div>
            )}

            {(editedService.assignedResources || []).length >= 2 && (
              <div className="client-choice-option">
                <div className="choice-section-title">¿Cómo elige el cliente el recurso?</div>
                <div className="radio-options">
                  <label className="radio-choice">
                    <input
                      type="radio"
                      name={`resourceChoice-${service.id}`}
                      value="required"
                      checked={editedService.resourceSelectionMode === 'required'}
                      onChange={() => handleChange('resourceSelectionMode', 'required')}
                    />
                    <div>
                      <span className="radio-label">Obligatorio elegir</span>
                      <span className="radio-help">El cliente debe elegir un recurso específico</span>
                    </div>
                  </label>
                  
                  <label className="radio-choice">
                    <input
                      type="radio"
                      name={`resourceChoice-${service.id}`}
                      value="optional"
                      checked={editedService.resourceSelectionMode === 'optional'}
                      onChange={() => handleChange('resourceSelectionMode', 'optional')}
                    />
                    <div>
                      <span className="radio-label">Opcional elegir</span>
                      <span className="radio-help">El cliente puede elegir o dejar que el sistema asigne</span>
                    </div>
                  </label>
                  
                  <label className="radio-choice">
                    <input
                      type="radio"
                      name={`resourceChoice-${service.id}`}
                      value="automatic"
                      checked={editedService.resourceSelectionMode === 'automatic' || !editedService.resourceSelectionMode}
                      onChange={() => handleChange('resourceSelectionMode', 'automatic')}
                    />
                    <div>
                      <span className="radio-label">Asignación automática</span>
                      <span className="radio-help">El sistema asigna automáticamente el recurso disponible</span>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>
          {pieDeParte("personal")}
        </PasoPlegable>
        )}

      </div>
    </div>
  );
};

export default EditableServiceCard; 